import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { isLinguaproz } from "@/config/mode";
import { TMS_BETA_URL } from "@/lib/links";
import { TIER_CHANGED_EVENT } from "@/lib/billingPlans";
import { LYRA_USED_EVENT, lyraUsedKey } from "@/components/GettingStartedCard";
import { useLyraContext } from "@/context/LyraContext";
import { useLinguaprozAuth } from "@/context/LinguaprozAuthContext";
import {
    Home,
    FileText,
    Settings,
    LogOut,
    Menu,
    X,
    BarChart3,
    Zap,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Sparkles,
    BookOpen,
    BookMarked,
    MessageSquare,
    HelpCircle,
    Layers,
    CreditCard,
    ExternalLink,
} from "lucide-react";

interface AuthenticatedLayoutProps {
    children: React.ReactNode;
}

const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
    children,
}) => {
    const { user: supabaseUser, signOut } = isLinguaproz
        ? { user: null, signOut: async () => {} }
        : useAuth();
    const lpAuth = isLinguaproz
        ? useLinguaprozAuth()
        : { user: null, logout: () => {} };
    const lpUser = lpAuth.user;
    const lpLogout = lpAuth.logout;
    const user = isLinguaproz ? lpUser : supabaseUser;
    const location = useLocation();

    // Detect active project from URL
    const editorMatch = location.pathname.match(/\/projects\/([^/]+)\/editor/);
    const activeProjectId = editorMatch?.[1] || null;
    const isInEditor = !!activeProjectId;

    // Last visited project, kept per user. The old shared key could show one
    // person's project to the next person using the same browser.
    const currentUserId = (user as any)?.id as string | undefined;
    const lastProjectKey = currentUserId
        ? `translyx_last_project_${currentUserId}`
        : null;
    const [lastProjectId, setLastProjectId] = useState<string | null>(null);

    useEffect(() => {
        try {
            localStorage.removeItem("translyx_last_project");
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        if (activeProjectId && lastProjectKey) {
            try {
                localStorage.setItem(lastProjectKey, activeProjectId);
            } catch {
                /* ignore */
            }
            setLastProjectId(activeProjectId);
        }
    }, [activeProjectId, lastProjectKey]);

    // Only offer "Back to Project" when the stored project still exists and
    // belongs to this user.
    useEffect(() => {
        if (!lastProjectKey || !currentUserId) {
            setLastProjectId(null);
            return;
        }
        let stored: string | null = null;
        try {
            stored = localStorage.getItem(lastProjectKey);
        } catch {
            /* ignore */
        }
        if (!stored) {
            setLastProjectId(null);
            return;
        }
        if (isLinguaproz) {
            setLastProjectId(stored);
            return;
        }
        let cancelled = false;
        (async () => {
            const { supabase } = await import("@/lib/supabase");
            const { data } = await supabase
                .from("cat_projects")
                .select("id")
                .eq("id", stored)
                .eq("owner_id", currentUserId)
                .maybeSingle();
            if (cancelled) return;
            if (data) {
                setLastProjectId(stored);
            } else {
                try {
                    localStorage.removeItem(lastProjectKey);
                } catch {
                    /* ignore */
                }
                setLastProjectId(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [lastProjectKey, currentUserId, location.pathname]);

    const currentProjectId = activeProjectId || lastProjectId;
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const { lyraOpen, setLyraOpen } = useLyraContext();
    useEffect(() => {
        if (!lyraOpen || !currentUserId) return;
        try {
            localStorage.setItem(lyraUsedKey(currentUserId), "1");
        } catch {
            /* ignore */
        }
        window.dispatchEvent(new Event(LYRA_USED_EVENT));
    }, [lyraOpen, currentUserId]);
    const [feedbackText, setFeedbackText] = useState("");
    const [feedbackSending, setFeedbackSending] = useState(false);
    const [userTier, setUserTier] = useState<string>("free");
    const [tierLoaded, setTierLoaded] = useState(false);
    const [tierRefreshKey, setTierRefreshKey] = useState(0);

    // Billing actions, returning to the tab, and navigating all re-read the
    // tier so the badge never shows a stale plan.
    useEffect(() => {
        const refresh = () => setTierRefreshKey((n) => n + 1);
        const onVisible = () => {
            if (document.visibilityState === "visible") refresh();
        };
        window.addEventListener(TIER_CHANGED_EVENT, refresh);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener(TIER_CHANGED_EVENT, refresh);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);
    const [fullName, setFullName] = useState<string>("");

    useEffect(() => {
        if (isLinguaproz) {
            setUserTier(lpUser?.tier || "free");
            setFullName(lpUser?.full_name || "");
            return;
        }
        if (!user?.id) return;
        (async () => {
            try {
                const { supabase } = await import("@/lib/supabase");
                const { data } = await supabase
                    .from("profiles")
                    .select("tier, full_name")
                    .eq("id", user.id)
                    .single();
                if (data?.tier) setUserTier(data.tier);
                if (data?.full_name) setFullName(data.full_name);
            } finally {
                setTierLoaded(true);
            }
        })();
    }, [user?.id, location.pathname, tierRefreshKey]);

    const handleFeedbackSubmit = async () => {
        if (!feedbackText.trim() || !user) return;
        if (isLinguaproz) return;
        setFeedbackSending(true);
        try {
            const { supabase } = await import("@/lib/supabase");
            await supabase.from("feedback").insert({
                user_id: user.id,
                message: feedbackText.trim(),
                page: location.pathname,
                created_at: new Date().toISOString(),
            });
            setFeedbackText("");
            setShowFeedback(false);
        } catch (e) {
            console.error("Feedback failed:", e);
        } finally {
            setFeedbackSending(false);
        }
    };

    const navigationItems = [
        { name: "Dashboard", href: "/dashboard", icon: Home },
        { name: "Projects", href: "/projects", icon: FileText },
        {
            name: "Translation Memory",
            href: "/translation-memory",
            icon: BookOpen,
        },
        { name: "Glossary", href: "/glossary", icon: BookMarked },
        { name: "Analytics", href: "/analytics", icon: BarChart3 },
        { name: "Settings", href: "/settings", icon: Settings },
        { name: "Help & Guide", href: "/help", icon: HelpCircle },
    ];

    const isActiveRoute = (href: string) =>
        href === "/dashboard"
            ? location.pathname === "/" || location.pathname === "/dashboard"
            : location.pathname.startsWith(href);

    const getPageTitle = () => {
        const p = location.pathname;
        if (p === '/dashboard' || p === '/') return 'Dashboard';
        if (p.startsWith('/projects/new')) return 'New Project';
        if (p.includes('/editor')) return 'Editor';
        if (p.startsWith('/projects')) return 'Projects';
        if (p.startsWith('/translation-memory')) return 'Translation Memory';
        if (p.startsWith('/glossary')) return 'Glossary';
        if (p.startsWith('/analytics')) return 'Analytics';
        if (p.startsWith('/billing')) return 'Plan & billing';
        if (p.startsWith('/settings')) return 'Settings';
        if (p.startsWith('/help')) return 'Help & Guide';
        if (p.startsWith('/admin')) return 'Admin';
        return 'TranslyxCAT';
    };

    const getUserDisplayName = () => {
        if (user?.name) return user.name;
        if (fullName) return fullName;
        if (user?.email) {
            const name = user.email.split("@")[0];
            return name.charAt(0).toUpperCase() + name.slice(1);
        }
        return "User";
    };

    const getInitials = () => {
        const source = (isLinguaproz ? lpUser?.full_name : getUserDisplayName()) || user?.email || "";
        const words = source.trim().split(/\s+/).filter(Boolean);
        if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
        return (words[0]?.[0] || "?").toUpperCase();
    };

    // Account menu: closes on Escape, outside click and route change; arrow
    // keys move between items; focus returns to the avatar.
    const [menuOpen, setMenuOpen] = useState(false);
    const avatarRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const closeMenu = useCallback((returnFocus: boolean) => {
        setMenuOpen(false);
        if (returnFocus) avatarRef.current?.focus();
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) return;
        const items = () =>
            Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []);
        items()[0]?.focus();
        const onMouseDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (menuRef.current?.contains(t) || avatarRef.current?.contains(t)) return;
            setMenuOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                closeMenu(true);
                return;
            }
            if (e.key === "Tab") {
                setMenuOpen(false);
                return;
            }
            const list = items();
            if (!list.length) return;
            const i = list.indexOf(document.activeElement as HTMLElement);
            let next = -1;
            if (e.key === "ArrowDown") next = (i + 1) % list.length;
            else if (e.key === "ArrowUp") next = (i - 1 + list.length) % list.length;
            else if (e.key === "Home") next = 0;
            else if (e.key === "End") next = list.length - 1;
            if (next >= 0) {
                e.preventDefault();
                list[next].focus();
            }
        };
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [menuOpen, closeMenu]);

    const handleLpSignOut = () => {
        lpLogout();
        // Confirmed with TMS — 17 June 2026 — translator's project list
        window.location.href = "https://translators.linguaproz.com/translator/projects";
    };

    const handleSignOut = async () => {
        try {
            try {
                if (lastProjectKey) localStorage.removeItem(lastProjectKey);
                localStorage.removeItem("translyx_last_project");
            } catch {
                /* ignore */
            }
            setLastProjectId(null);
            await signOut();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* ── SIDEBAR ─────────────────────────────────── */}
            <div
                className={`
        fixed inset-y-0 left-0 z-50 bg-slate-900 flex flex-col
        transition-all duration-300 ease-in-out h-screen overflow-hidden
        ${collapsed ? "w-16" : "w-64"}
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 lg:sticky lg:top-0 lg:self-start
      `}
            >
                {/* Logo row */}
                <div className="h-16 flex items-center justify-between px-3 border-b border-slate-700 shrink-0">
                    {!collapsed && (
                        <Link
                            to="/dashboard"
                            className="flex items-center min-w-0"
                        >
                            <span className="text-lg font-bold tracking-wide truncate">
                                <span className="text-white">Translyx</span>
                                <span className="text-gold-400">CAT</span>
                            </span>
                        </Link>
                    )}
                    {collapsed && (
                        <Link to="/dashboard" className="mx-auto">
                            <span className="text-xs font-bold text-gold-400 tracking-widest">
                                T
                            </span>
                        </Link>
                    )}
                    {/* Mobile close */}
                    <button
                        onClick={() => setMobileOpen(false)}
                        className="lg:hidden text-white hover:text-gold-600 ml-2"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Back to Project — shown when a project is open */}
                {currentProjectId && !isInEditor && (
                    <div className="mx-2 mt-2 flex items-center gap-1">
                        <Link
                            to={`/projects/${currentProjectId}/editor`}
                            className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-gold-600 hover:bg-gold-500 text-white text-sm font-medium transition-colors"
                        >
                            <FileText className="h-4 w-4 shrink-0" />
                            {!collapsed && (
                                <span className="truncate">
                                    Back to Project
                                </span>
                            )}
                        </Link>
                        {!collapsed && (
                            <button
                                onClick={() => {
                                    if (lastProjectKey) {
                                        localStorage.removeItem(lastProjectKey);
                                    }
                                    setLastProjectId(null);
                                }}
                                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors shrink-0"
                                title="Dismiss"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                )}
                {/* Nav */}
                {!isLinguaproz && (
                    <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
                        {navigationItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = isActiveRoute(item.href);
                            return (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    onClick={() => setMobileOpen(false)}
                                    title={collapsed ? item.name : undefined}
                                    className={`
                  flex items-center rounded-lg transition-colors
                  ${collapsed ? "justify-center px-2 py-1.5" : "space-x-3 px-3 py-1.5"}
                  ${
                      isActive
                          ? "bg-slate-700/60 text-gold-400 font-medium border-l-2 border-gold-500"
                          : "text-slate-300 hover:text-white hover:bg-slate-700/50 border-l-2 border-transparent"
                  }
                `}
                                >
                                    <Icon className="h-4 w-4 shrink-0" />
                                    {!collapsed && (
                                        <span className="truncate text-sm">
                                            {item.name}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}

                        {/* TranslyxTMS beta: external early access link, never "active" */}
                        <a
                            href={TMS_BETA_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="TranslyxTMS, Beta, opens the early access page in a new tab"
                            title={collapsed ? "TranslyxTMS (Beta)" : undefined}
                            className={`
                  flex items-center rounded-lg transition-colors
                  ${collapsed ? "justify-center px-2 py-1.5" : "space-x-3 px-3 py-1.5"}
                  text-slate-300 hover:text-white hover:bg-slate-700/50 border-l-2 border-transparent
                `}
                        >
                            <Layers className="h-4 w-4 shrink-0" />
                            {!collapsed && (
                                <>
                                    <span className="truncate text-sm">
                                        TranslyxTMS
                                    </span>
                                    <span
                                        className="rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none text-[#F3D3A2]"
                                        style={{ background: "rgba(226,178,115,0.25)" }}
                                    >
                                        Beta
                                    </span>
                                    <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 opacity-70" />
                                </>
                            )}
                        </a>
                    </nav>
                )}
                {/* No Dashboard nav item in LP mode — translators have no CAT-side dashboard, only translators.linguaproz.com */}
                {isLinguaproz && <nav className="flex-1 px-3 py-4 space-y-1" />}

                {/* User + collapse toggle */}
                <div className="border-t border-slate-700 p-2 shrink-0">
                    {!isLinguaproz && (
                        <>
                            <button
                                onClick={handleSignOut}
                                title="Sign Out"
                                className={`w-full flex items-center rounded-lg px-2 py-2 text-white hover:text-gold-400 hover:bg-slate-700 transition-colors ${collapsed ? "justify-center" : "space-x-2"}`}
                            >
                                <LogOut className="h-3.5 w-3.5 shrink-0" />
                                {!collapsed && (
                                    <span className="text-xs">Sign Out</span>
                                )}
                            </button>
                        </>
                    )}
                    {isLinguaproz && lpUser && (
                        <div className="px-3 py-3 border-t border-slate-700">
                            <button
                                onClick={handleLpSignOut}
                                className="flex items-center gap-2 w-full px-3 py-2 text-xs text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                            >
                                <LogOut className="h-3.5 w-3.5" />
                                Back to My Projects
                            </button>
                        </div>
                    )}

                    {/* Feedback button */}
                    <button
                        onClick={() => setShowFeedback(true)}
                        title="Send Feedback"
                        className={`w-full flex items-center rounded-lg px-2 py-2 text-slate-400 hover:text-gold-400 hover:bg-slate-700 transition-colors ${collapsed ? "justify-center" : "space-x-2"}`}
                    >
                        <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                        {!collapsed && (
                            <span className="text-xs">Feedback</span>
                        )}
                    </button>

                    {/* Feedback modal */}
                    {showFeedback && (
                        <div
                            className="fixed inset-0 bg-black/50 z-50 flex items-end justify-start p-4 lg:items-center lg:justify-center"
                            onClick={() => setShowFeedback(false)}
                        >
                            <div
                                className="bg-white rounded-2xl shadow-2xl p-5 w-80"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-slate-900">
                                        Send Feedback
                                    </h3>
                                    <button
                                        onClick={() => setShowFeedback(false)}
                                        className="p-1 hover:bg-slate-100 rounded"
                                    >
                                        <X className="w-4 h-4 text-slate-400" />
                                    </button>
                                </div>
                                <textarea
                                    value={feedbackText}
                                    onChange={(e) =>
                                        setFeedbackText(e.target.value)
                                    }
                                    placeholder="Tell us what you think..."
                                    className="w-full border border-slate-200 rounded-lg p-2.5 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-gold-500"
                                    autoFocus
                                />
                                <button
                                    onClick={handleFeedbackSubmit}
                                    disabled={
                                        feedbackSending || !feedbackText.trim()
                                    }
                                    className="mt-2 w-full py-2 bg-gold-600 hover:bg-gold-500 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                                >
                                    {feedbackSending
                                        ? "Sending..."
                                        : "Send Feedback"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Collapse toggle placeholder — actual tab rendered outside sidebar */}
                </div>
            </div>

            {/* Sidebar edge toggle tab — desktop only */}
            <button
                onClick={() => setCollapsed((c) => !c)}
                title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                className="hidden lg:flex fixed z-50 flex-col items-center justify-center w-6 h-14 rounded-r-xl bg-slate-700 hover:bg-gold-500 border border-l-0 border-slate-600 hover:border-gold-400 text-slate-300 hover:text-white transition-all duration-300 shadow-lg"
                style={{ top: "50%", transform: "translateY(-50%)", left: collapsed ? "63px" : "255px" }}
            >
                {collapsed ? (
                    <ChevronsRight className="h-4 w-4 stroke-[2.5]" />
                ) : (
                    <ChevronsLeft className="h-4 w-4 stroke-[2.5]" />
                )}
            </button>

            {/* ── MAIN CONTENT ────────────────────────────── */}
            <div className="flex-1 flex flex-col min-h-screen min-w-0">
                {/* Top bar — desktop + mobile */}
                {/* Top bar hidden in editor */}
                {!isInEditor && (
                <header
                    className="topbar"
                    style={{ marginRight: !isLinguaproz && lyraOpen ? "380px" : 0, transition: "margin-right 0.3s ease" }}
                >
                    <button
                        onClick={() => setMobileOpen(true)}
                        className="lg:hidden text-slate-600 hover:text-slate-900 mr-auto"
                        aria-label="Open navigation"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                    {!isLinguaproz && (
                        <button
                            onClick={() => setLyraOpen(o => !o)}
                            className={"flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border " + (
                                lyraOpen
                                    ? "bg-slate-900 text-gold-400 border-slate-700"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-gold-400 hover:text-gold-600"
                            )}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Ask Lyra
                        </button>
                    )}
                    {!isLinguaproz && tierLoaded && userTier === "free" && (
                        <Link
                            to="/billing"
                            className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-[#E2B273] text-[#1E293B] hover:brightness-105 transition"
                        >
                            Upgrade
                        </Link>
                    )}
                    {user && (
                        <div className="relative">
                            <button
                                ref={avatarRef}
                                type="button"
                                onClick={() => setMenuOpen((o) => !o)}
                                aria-label="Account menu"
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                className="topbar-avatar"
                                style={{ borderColor: menuOpen ? "#E2B273" : "#E2E8F0" }}
                            >
                                {getInitials()}
                            </button>
                            {menuOpen && (
                                <div ref={menuRef} role="menu" aria-label="Account" className="acct-menu">
                                    <div className="acct-head">
                                        <div className="acct-name">{isLinguaproz ? lpUser?.full_name || lpUser?.email : getUserDisplayName()}</div>
                                        {(!isLinguaproz || lpUser?.full_name) && user?.email && (
                                            <div className="acct-email">{user.email}</div>
                                        )}
                                        {!isLinguaproz && tierLoaded && (
                                            <span className="acct-plan">
                                                {userTier.charAt(0).toUpperCase() + userTier.slice(1)} plan
                                            </span>
                                        )}
                                    </div>
                                    {!isLinguaproz && (
                                        <>
                                            <Link to="/billing" role="menuitem" tabIndex={-1} className="acct-item" onClick={() => closeMenu(false)}>
                                                <CreditCard className="h-4 w-4" aria-hidden="true" />
                                                Plan &amp; billing
                                            </Link>
                                            <Link to="/settings" role="menuitem" tabIndex={-1} className="acct-item" onClick={() => closeMenu(false)}>
                                                <Settings className="h-4 w-4" aria-hidden="true" />
                                                Settings
                                            </Link>
                                            <Link to="/help" role="menuitem" tabIndex={-1} className="acct-item" onClick={() => closeMenu(false)}>
                                                <HelpCircle className="h-4 w-4" aria-hidden="true" />
                                                Help &amp; Guide
                                            </Link>
                                            <div className="acct-divider" role="separator" />
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        role="menuitem"
                                        tabIndex={-1}
                                        className="acct-item"
                                        onClick={() => {
                                            closeMenu(false);
                                            if (isLinguaproz) handleLpSignOut();
                                            else handleSignOut();
                                        }}
                                    >
                                        <LogOut className="h-4 w-4" aria-hidden="true" />
                                        Sign out
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </header>
                )}

                <main className="flex-1 overflow-y-auto bg-gray-50 min-w-0" style={{ marginRight: (!isInEditor && lyraOpen) ? "380px" : 0, transition: "margin-right 0.3s ease" }}>
                    {children}
                </main>
            </div>

            {/* Mobile overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setMobileOpen(false)}
                />
            )}
        </div>
    );
};

export default AuthenticatedLayout;
